import { useState } from "react";
import { Landmark } from "lucide-react";
import { isSMSF } from "../../lib/calculations";
import type { PropertyRecord } from "../../types/portfolio";
import type { Structure, InvestorProfile } from "../../types/entities";
import type { BudgetSettings } from "../../types/budget";
import type { AppSettings } from "../settings/SettingsPage";
import { supabase, isSupabaseEnabled } from "../../lib/supabase";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { InfoTooltip } from "../ui/InfoTooltip";
import { TabExplainer } from "../ui/TabExplainer";
import PortfolioFlow from "./PortfolioFlow";
import BucketFlow from "./BucketFlow";
import SuperFlow from "./SuperFlow";

interface TransfersPageProps {
  budgetSettings: BudgetSettings;
  portfolioProperties: PropertyRecord[];
  portfolioStructures: Structure[];
  appSettings: AppSettings;
  profile: InvestorProfile;
  onUpdateStructure?: (s: Structure) => void;
  onUpdateProperty?: (p: PropertyRecord) => void;
  onNavigateToLoans?: () => void;
  onNavigateToTax?: () => void;
  onUpdateAppSettings?: (s: AppSettings) => void;
  monthlySurplus?: number;
  userEmail?: string;
  includeSMSFInPortfolio: boolean;
}

export default function TransfersPage({
  budgetSettings,
  portfolioProperties,
  portfolioStructures,
  appSettings,
  profile,
  onUpdateStructure,
  onUpdateProperty,
  onNavigateToLoans,
  onNavigateToTax,
  onUpdateAppSettings,
  monthlySurplus = 0,
  userEmail,
  includeSMSFInPortfolio,
}: TransfersPageProps) {
  const showBankFeeds = useFeatureFlag("bank_feeds", userEmail);

  const committedProperties = portfolioProperties.filter(
    (p) => !p.notes?.includes("[DRAFT SCENARIO]")
  );

  // Section order: when any non-property bucket is prioritised above the property bucket,
  // render Bucket Flow before Funding Sources to match the user's priority ordering.
  const propertyIndex = budgetSettings.buckets.findIndex((b) => b.type === "property");
  const firstNonPropertyIndex = budgetSettings.buckets.findIndex((b) => b.type !== "property");
  const bucketsAboveProperty = firstNonPropertyIndex !== -1 && propertyIndex !== -1 && firstNonPropertyIndex < propertyIndex;

  const portfolioBlock = (
    <div key="portfolio" className="rounded-2xl bg-neutral-50 p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-neutral-900">Funding Sources</h2>
        <p className="mt-1 text-sm text-neutral-500">Amounts drawn from credit facilities each pay cycle to fund your portfolio top-up.</p>
      </div>
      <PortfolioFlow
        structures={portfolioStructures}
        properties={committedProperties}
        budgetSettings={budgetSettings}
        appSettings={appSettings}
        profile={profile}
        includeSMSF={includeSMSFInPortfolio}
        onUpdateStructure={onUpdateStructure}
        onUpdateAppSettings={onUpdateAppSettings}
        onUpdateProperty={onUpdateProperty}
        onNavigateToLoans={onNavigateToLoans}
        onNavigateToTax={onNavigateToTax}
        monthlySurplus={monthlySurplus}
      />
    </div>
  );

  const bucketBlock = (
    <div key="buckets" className="rounded-2xl bg-neutral-50 p-6">
      <div className="mb-4">
        <h2 className="flex items-center gap-1 text-lg font-semibold text-neutral-900">
          Bucket Flow
          <InfoTooltip tip="Where your non-property monthly surplus allocations move each month. Each bucket has a nominated destination bank account." />
        </h2>
        <p className="mt-1 text-sm text-neutral-500">Non-property bucket allocations flowing from your monthly surplus.</p>
      </div>
      <BucketFlow budgetSettings={budgetSettings} monthlySurplus={monthlySurplus} />
    </div>
  );

  return (
    <div className="space-y-8">

      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold text-neutral-900">Cashflow & Transfers</h1>
        <p className="mt-2 text-neutral-600">
          Where money physically moves each month, from source accounts to destination accounts.
        </p>
      </div>

      <TabExplainer>
        <p>
          <strong>Cashflow & Transfers — Where money physically moves each month</strong>
        </p>
        <p>
          This page is your monthly money map. It shows every dollar flowing into your property portfolio
          each month — and where it comes from. Three sections, each answering a different question:
        </p>
        <p>
          <strong>1. Funding Sources</strong> — How is each property's monthly shortfall actually paid?
          Personal contribution from your surplus, equity release drawdowns from secured credit facilities,
          PAYG variation refunds, and surplus redistribution from positively-geared entities. Every source
          sits on the left; every destination entity sits on the right; the total flowing through is in the
          centre. Click any source card to expand its configuration inline.
        </p>
        <p>
          <strong>2. Bucket Flow</strong> — Where does your monthly surplus go after the property portfolio
          is funded? Holidays, splurge accounts, savings, sinking fund. Strict waterfall priority — the
          bucket at the top of the list on the Personal Budget page gets funded first. Drag to reorder.
        </p>
        <p>
          <strong>3. Super Flow</strong> — For SMSF-held properties: contributions in, property cashflow
          out, net fund position. Useful for tracking whether the SMSF can self-fund or needs a contribution
          top-up.
        </p>
        <p>
          <strong>Use this page when:</strong> setting up direct debits and recurring transfers; deciding
          whether to apply for a PAYG variation; debugging a cashflow shortfall; planning a new equity
          release; reordering bucket priorities mid-year.
        </p>
        <p className="text-amber-700">
          Numbers are monthly equivalents derived from your portfolio configuration — they show the
          target flow, not what your bank account actually did. Reconcile against bank statements
          quarterly.
        </p>
      </TabExplainer>

      {bucketsAboveProperty ? <>{bucketBlock}{portfolioBlock}</> : <>{portfolioBlock}{bucketBlock}</>}

      {/* ── Super Flow ─────────────────────────────────────────── */}
      {(() => {
        const smsfExists = portfolioStructures.some((s) => isSMSF(s.entityType));
        if (!smsfExists) return null;
        return (
          <div className="rounded-2xl bg-neutral-50 p-6">
            <div className="mb-4">
              <h2 className="flex items-center gap-1 text-lg font-semibold text-neutral-900">
                Super Flow
                <InfoTooltip tip="Super contributions flowing into your SMSF and funding SMSF property investments. SMSF cashflow is legally separate from personal portfolio funding." />
              </h2>
              <p className="mt-1 text-sm text-neutral-500">SMSF contributions funding property investments inside super.</p>
            </div>
            <SuperFlow structures={portfolioStructures} properties={committedProperties} />
          </div>
        );
      })()}

      {/* Transfers by Entity + SMSF Funds sections deleted Phase 6c-3a.
          Content now lives inside Portfolio Flow (entities) + Super Flow (SMSF). */}
      {/* ── Bank Feeds Preview — gated by bank_feeds feature flag ──────────── */}
      {showBankFeeds && <BankFeedsPreview />}
    </div>
  );
}

// ─── Bank Feeds preview section ───────────────────────────────────────────────

function BankFeedsPreview() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  async function handleJoin() {
    const trimmed = email.trim();
    if (!trimmed || !/\S+@\S+\.\S+/.test(trimmed)) return;
    setStatus("loading");
    try {
      if (isSupabaseEnabled() && supabase) {
        const { data: { user } } = await supabase.auth.getUser();
        await supabase.from("bank_feed_waitlist").upsert(
          { email: trimmed, user_id: user?.id ?? null, created_at: new Date().toISOString() },
          { onConflict: "email", ignoreDuplicates: true }
        );
      }
      setStatus("success");
    } catch {
      setStatus("success"); // Show success even if DB fails — don't block UX
    }
  }

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-5">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-neutral-100">
          <Landmark size={20} className="text-neutral-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-neutral-900">Live Bank Feeds — Coming Soon</h2>
          <p className="text-xs text-neutral-400">Phase 5 — currently in development</p>
        </div>
      </div>

      <p className="text-sm text-neutral-600 leading-relaxed">
        Live bank feeds will automatically import your rental income and property expenses directly
        from your bank accounts — eliminating manual entry and keeping your cashflow data accurate
        in real time.
      </p>

      <div className="rounded-xl bg-neutral-50 border border-neutral-100 p-4 space-y-2">
        <p className="text-sm font-medium text-neutral-800">What you'll be able to do:</p>
        <ul className="space-y-1.5 text-sm text-neutral-600">
          {[
            "See actual rent received vs budgeted — per property",
            "Auto-categorise property expenses (repairs, rates, insurance)",
            "Match bank transactions to your AusProp properties",
            "Spot missed rent payments the day they're due",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1 flex-shrink-0 w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center">
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
              </span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      <p className="text-sm text-neutral-600 leading-relaxed">
        <span className="font-medium">How it works:</span> AusProp will connect via{" "}
        <a href="https://basiq.io" target="_blank" rel="noopener noreferrer" className="underline hover:text-neutral-800">Basiq</a>
        {" "}— Australia's leading open banking platform, used by NAB, ANZ, Westpac, CBA and 150+ other
        institutions. Your bank credentials are never stored by AusProp. Connection is read-only.
      </p>

      {/* Waitlist */}
      <div className="border-t border-neutral-100 pt-4">
        <p className="text-sm font-medium text-neutral-800 mb-3">Get notified when bank feeds launch</p>
        {status === "success" ? (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
            <p className="text-sm text-emerald-700 font-medium">You're on the list. We'll email you when bank feeds are ready.</p>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void handleJoin()}
              className="flex-1 rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
            <button
              type="button"
              onClick={() => void handleJoin()}
              disabled={status === "loading"}
              className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-50 transition-colors"
            >
              {status === "loading" ? "Joining…" : "Join waitlist"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

